/* eslint-disable no-underscore-dangle */
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useDevUi } from '@utils/DevUIPanel/DevUiProvider'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { loadMfaFactorsFromMultipleSources } from '@utils/mfaUtils'
import { useOkta } from '@hooks/useOkta'
import { useSiteContext } from '@contexts/MkSiteContext'
import { useSnackbar } from '@contexts/SnackbarContext'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { EMkAuthenticationAction } from '@auth/types'
import { stepOne, StepOnePayload } from '@api/registration'
import { StepName } from '@account/components/Register/types'
import { useRegistration } from '@account/hooks/useRegistration'
import { useEmailVerification } from '@account/hooks/useRegistration/useEmailVerification'
import { RegistrationContext } from '@account/hooks/useRegistration/_reducer'
import { getStepByName, getStepNames, getStepValidationErrorsByName, validateStepByName as stepValidator } from '@account/components/Register/stepRegistry'
import { normalizeApiError } from '@utils/errorNormalizer'
import { mergeRegistrationMfaResendHref } from '@utils/oktaRegistrationMfaLinks'
import { classifyMfaVerifyError, EMfaVerifyActionKind } from '@account/utils/classifyMfaVerifyError'
import { RegisterContext } from './_context'
import {
  OKTA_STATUS,
  OKTA_DOMAIN,
  OKTA_WELCOME_PATH,
  RECOVERY_ERROR_CODES,
  HTTP_HEADERS,
  CONTENT_TYPES,
  REGISTRATION_STORAGE_KEYS,
  SESSION_TYPES,
  PLACEHOLDER_VALUES,
  TIME_CONSTANTS,
  NAVIGATION_PATHS,
  VALIDATION_THRESHOLDS,
  API_RESPONSE_FIELDS,
  SESSION_FIELDS,
  ERROR_CHECK_FIELDS,
  REGISTER_LABELS,
} from '../../../../shared/constants/account'
import { OKTA_CACHE_STORAGE, OKTA_SHARED_TRANSACTION_STORAGE, OKTA_ORIGINAL_URI_STORAGE, OKTA_TOKEN_STORAGE, OKTA_TOKEN_STORAGE_KEYS } from '../../../../shared/constants/core'

export const RegisterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const { authBrand: brand } = useSiteContext()
  const { showSnackbar } = useSnackbar()
  const { dispatchAuthenticationAction, isMfaLocked, mfaLockoutEndTime } = useAuthentication()

  const [, setLocalMfaAttempts] = React.useState(0)
  /** Accurate count across sequential verify submits (avoids stale closure on submit callback). */
  const localMfaAttemptsRef = React.useRef(0)
  /** Prevents double-submit on MFA verify (rapid Confirm clicks). */
  const mfaVerifySubmitInFlightRef = React.useRef(false)
  const {
    state,
    updateField,
    setFieldTouched,
    setCurrentStep,
    setLoading,
    setError,
    clearError,
    setRegistrationToken,
    setValidationErrors,
    canSubmit,
    setFieldError,
    gotoStepByName,
    setSelectedFactor,
    setMfaFactors,
    setUser,
    setMfaEnabled,
    registrationMfaEnabled,
    saveRegistrationContext,
    clearRegistrationContext,
    loadRegistrationContext,
    resetState,
    dispatch,
    gotoStep,
    getStepIndexByName,
    gotoActivationError,
    hasValidRegistrationContext,
    setSubmitted,
  } = useRegistration()

  const dev = useDevUi()
  const { oktaAuth } = useOkta()

  useEmailVerification()

  React.useEffect(() => {
    if (state.currentStepName === StepName.MFA_SETUP_SELECTOR || state.currentStepName === StepName.MFA_VERIFY_CODE) {
      localMfaAttemptsRef.current = 0
      setLocalMfaAttempts(0)
    }
  }, [state.currentStepName])

  React.useEffect(() => {
    const handleModalClose = () => {
      // Ensure each new modal session starts MFA attempt counting from zero.
      localMfaAttemptsRef.current = 0
      setLocalMfaAttempts(0)
    }
    window.addEventListener('modalClose', handleModalClose)
    return () => {
      window.removeEventListener('modalClose', handleModalClose)
    }
  }, [])

  const persistMfaChallengeFromError = React.useCallback(
    (apiError: any) => {
      if (state.currentStepName !== StepName.MFA_VERIFY_CODE) return

      const raw = apiError?.raw && typeof apiError.raw === 'object' ? apiError.raw : apiError
      const stateToken = raw?.[API_RESPONSE_FIELDS.STATE_TOKEN] || apiError?.[API_RESPONSE_FIELDS.STATE_TOKEN]
      if (!stateToken) return

      updateField(REGISTRATION_STORAGE_KEYS.STATE_TOKEN, stateToken)
      DataStore.setItem(REGISTRATION_STORAGE_KEYS.STATE_TOKEN, stateToken)

      const links = raw?.[API_RESPONSE_FIELDS.LINKS] || apiError?.[API_RESPONSE_FIELDS.LINKS]
      const resendLink = Array.isArray(links?.resend) ? links.resend[0]?.[API_RESPONSE_FIELDS.HREF] : links?.resend?.[API_RESPONSE_FIELDS.HREF]
      const verifyLink = Array.isArray(links?.[API_RESPONSE_FIELDS.VERIFY])
        ? links[API_RESPONSE_FIELDS.VERIFY][0]?.[API_RESPONSE_FIELDS.HREF]
        : links?.[API_RESPONSE_FIELDS.VERIFY]?.[API_RESPONSE_FIELDS.HREF]
      const factor = raw?.[API_RESPONSE_FIELDS.EMBEDDED]?.[API_RESPONSE_FIELDS.FACTOR] || apiError?.[API_RESPONSE_FIELDS.EMBEDDED]?.[API_RESPONSE_FIELDS.FACTOR]
      const currentContext = loadRegistrationContext() || ({} as Partial<RegistrationContext>)

      saveRegistrationContext({
        ...currentContext,
        selectedFactor: factor || currentContext.selectedFactor,
        factorProfile: factor?.profile || currentContext.factorProfile,
        mfaEmail: factor?.factorType === 'email' ? factor.profile?.email : currentContext.mfaEmail,
        phone: factor?.factorType === 'sms' || factor?.factorType === 'call' ? factor.profile?.phoneNumber : currentContext.phone,
        resendLink: mergeRegistrationMfaResendHref(resendLink, verifyLink, currentContext.resendLink),
        verifyLink: verifyLink || currentContext.verifyLink,
        stateToken,
        activationToken: currentContext.activationToken || '',
        registrationToken: currentContext.registrationToken || '',
        email: currentContext.email || '',
        userName: currentContext.userName || '',
      })
    },
    [loadRegistrationContext, saveRegistrationContext, state.currentStepName, updateField],
  )

  const submit = React.useCallback(
    async (stepName?: string, newPayload?: any): Promise<void> => {
      if (state.currentStepName === StepName.MFA_VERIFY_CODE && isMfaLocked) {
        if (mfaLockoutEndTime && Date.now() < mfaLockoutEndTime) {
          resetState()
          setCurrentStep(StepName.ACCOUNT_LOCK_SCREEN)
          return
        }
        dispatchAuthenticationAction({ type: EMkAuthenticationAction.UNLOCK_MFA_ACCOUNT })
        dispatchAuthenticationAction({ type: EMkAuthenticationAction.RESET_FAILED_MFA_ATTEMPTS })
      }

      if (!dev.enabled || !dev.bypassValidation) {
        const { currentStepName } = state
        const isValid = stepName === StepName.ACCOUNT_LOOKUP ? true : stepValidator(currentStepName, state.formData as any)
        const isMfaVerificationStep = currentStepName === StepName.MFA_VERIFY_CODE

        if (!isValid && !isMfaVerificationStep) {
          setValidationErrors(getStepValidationErrorsByName(currentStepName, state.formData as any))
          return
        }

        if (isValid || isMfaVerificationStep) {
          setValidationErrors([])
        }
      } else {
        setValidationErrors([])
      }

      if (state.currentStepName === StepName.MFA_VERIFY_CODE) {
        const code = String(state.formData.verificationCode?.value ?? '').trim()
        // Never run MFA verify API with an empty code (avoids spurious submit on mount / focus quirks).
        if (!code) {
          return
        }
        if (mfaVerifySubmitInFlightRef.current) {
          return
        }
        mfaVerifySubmitInFlightRef.current = true
      }

      setLoading(true)

      try {
        const { currentStepName } = state
        const current = getStepByName(currentStepName)
        if (!current) throw new Error('No step found for current step name')

        const { apiConfig } = current
        if (!apiConfig) {
          if (currentStepName === StepName.MFA_SETUP_SELECTOR) {
            const isEmailFactor = state.selectedFactor?.factorType === 'email'
            const regCtx = loadRegistrationContext()
            const emailValue = String(state.formData.email?.value || state.formData.mfaEmail?.value || '')
            const ctxEmail = String(regCtx?.email || regCtx?.mfaEmail || '')
            const hasValidEmail =
              (emailValue && String(emailValue).includes('@') && emailValue.indexOf('@') > 0) || (ctxEmail && ctxEmail.includes('@') && ctxEmail.indexOf('@') > 0)

            if (isEmailFactor && hasValidEmail) {
              return
            }
          }

          setValidationErrors([])
          clearError()
          const stepNames = getStepNames()
          const currentIndex = stepNames.indexOf(state.currentStepName)
          if (currentIndex < stepNames.length - 1) {
            gotoStepByName(stepNames[currentIndex + 1])
          }
          return
        }
        const { endpoint, method, headers = {}, params = {}, body, onSuccess } = apiConfig

        const baseHeaders = {
          [HTTP_HEADERS.ACCEPT]: CONTENT_TYPES.JSON,
          [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
          [HTTP_HEADERS.BRAND]: brand,
        }

        const buildUrl = (url: string | URL, qp: Record<string, string | null>) => {
          const urlObj = typeof url === 'string' ? new URL(url) : url
          Object.entries(qp).forEach(([k, v]) => v && urlObj.searchParams.append(k, v))
          return urlObj
        }

        const buildApiError = async (response: Response) => {
          const contentType = (typeof response.headers?.get === 'function' ? response.headers.get('content-type') : null) || ''
          const isJsonResponse = contentType.includes(CONTENT_TYPES.JSON)
          let parsed: unknown
          if (isJsonResponse) {
            parsed = await response.json().catch(() => ({}))
          } else if (typeof response.text === 'function') {
            parsed = await response.text().catch(() => '')
          } else if (typeof response.json === 'function') {
            parsed = await response.json().catch(() => ({}))
          } else {
            parsed = {}
          }
          const normalized = normalizeApiError(parsed)
          const parsedObj = typeof parsed === 'object' && parsed != null ? (parsed as Record<string, unknown>) : null
          const oktaSummary = parsedObj && typeof parsedObj.errorSummary === 'string' ? parsedObj.errorSummary : ''
          let userFacingMessage = oktaSummary || normalized.message || `HTTP ${response.status}`
          const parsedStringBody = typeof parsed === 'string' ? parsed.trim() : ''
          const parsedEmptyObject = parsedObj != null && !Array.isArray(parsedObj) && Object.keys(parsedObj).length === 0 && !response.ok
          const shouldHideNonJsonBody = !isJsonResponse && parsedStringBody.length > 0
          if (parsedEmptyObject || userFacingMessage === '{}' || shouldHideNonJsonBody) {
            userFacingMessage = REGISTER_LABELS.REGISTRATION_VERIFY_HTTP_BODY_INVALID
          }

          const error = new Error(userFacingMessage) as Error & {
            code?: string
            errorCode?: string
            errorSummary?: string
            errors?: Array<{ field?: string; message: string }>
            raw?: unknown
            statusCode?: number
            stateToken?: string
          }

          if (oktaSummary) {
            error.errorSummary = oktaSummary
          }

          if (normalized.code) {
            error.code = normalized.code
            error.errorCode = normalized.code
          }
          if (normalized.errors) error.errors = normalized.errors
          error.raw = normalized.raw ?? parsed
          if (parsedObj && typeof parsedObj[API_RESPONSE_FIELDS.STATE_TOKEN] === 'string') {
            error.stateToken = parsedObj[API_RESPONSE_FIELDS.STATE_TOKEN]
          }
          error.statusCode = response.status

          return error
        }

        const stateTokenToPass = (state as any).stateToken || (state.formData as any)?.stateToken?.value || null

        const requestBody = method !== 'GET' ? body?.(newPayload || (state.formData as any), state.registrationToken, stateTokenToPass, state.selectedFactor) : undefined

        let data: any
        const submitStepName = current.name as StepName
        if (submitStepName === StepName.ACCOUNT_LOOKUP || (data == null && state.currentStepName === StepName.ACCOUNT_LOOKUP)) {
          data = await stepOne(requestBody as StepOnePayload, brand)

          // Save firstName and lastName to formData so they can be used in the title
          if (requestBody?.firstName) {
            updateField('firstName', requestBody.firstName)
          }
          if (requestBody?.lastName) {
            updateField('lastName', requestBody.lastName)
          }
          if (requestBody?.membershipNumber) {
            updateField('ownerNumber', requestBody.membershipNumber)
          }

          const existingContext = loadRegistrationContext()
          // Check URL for activation token - if no token in URL, this is a new registration and we should clear old context
          const urlParams = new URLSearchParams(window.location.search)
          const hasActivationTokenInUrl = urlParams.get('token') || urlParams.get('at')
          // Only preserve context if we're actually in an activation flow (token in URL)
          const isActivationFlow = hasActivationTokenInUrl || (existingContext?.isActivationFlow && hasActivationTokenInUrl)
          if (!isActivationFlow) {
            clearRegistrationContext()
            try {
              localStorage.removeItem('makoRegistrationContext')
            } catch (error) {
              // Ignore errors
            }
          }
        } else if (data == null) {
          const resolvedEndpoint = typeof endpoint === 'function' ? endpoint(state.formData as any, state.registrationToken) : endpoint
          const requestUrl = method === 'GET' ? buildUrl(resolvedEndpoint, params) : resolvedEndpoint
          const response = await fetch(requestUrl, {
            method,
            headers: { ...baseHeaders, ...headers },
            ...(requestBody && { body: JSON.stringify(requestBody) }),
            redirect: 'manual',
          })

          if (response.status >= 300 && response.status < 400) {
            const redirectUrl = (typeof response.headers?.get === 'function' ? response.headers.get('Location') : null) || response.url
            if (redirectUrl) {
              let parsedRedirectUrl: URL | null = null
              try {
                parsedRedirectUrl = new URL(redirectUrl, window.location.origin)
              } catch {
                parsedRedirectUrl = null
              }

              const isOktaDomain = parsedRedirectUrl != null && parsedRedirectUrl.hostname === OKTA_DOMAIN
              const isWelcomePath = parsedRedirectUrl != null && parsedRedirectUrl.pathname.includes(OKTA_WELCOME_PATH)

              if (parsedRedirectUrl && (isWelcomePath || isOktaDomain)) {
                const error = new Error('Your session has expired. Please request a new activation link.')
                Object.assign(error, {
                  errorCode: RECOVERY_ERROR_CODES.OKTA_INVALID_TOKEN,
                  errorSummary: 'Token expired or invalid',
                  redirectUrl,
                })
                throw error
              }
            }
            const error = new Error('Unexpected redirect received. Please try again.')
            Object.assign(error, {
              errorCode: 'REDIRECT_ERROR',
              errorSummary: 'Unexpected redirect',
              redirectUrl,
            })
            throw error
          }

          if (!response.ok) {
            throw await buildApiError(response)
          }
          data = await response.json()
        }
        if (data?.[API_RESPONSE_FIELDS.PARTY]?.[API_RESPONSE_FIELDS.REGISTRATION_TOKEN]) {
          setRegistrationToken(data[API_RESPONSE_FIELDS.PARTY][API_RESPONSE_FIELDS.REGISTRATION_TOKEN])
        } else if (data?.[API_RESPONSE_FIELDS.PARTY] && typeof data[API_RESPONSE_FIELDS.PARTY] === 'string') {
          setRegistrationToken(data[API_RESPONSE_FIELDS.PARTY])
        }
        if (data?.[API_RESPONSE_FIELDS.STATE_TOKEN]) {
          updateField(REGISTRATION_STORAGE_KEYS.STATE_TOKEN, data[API_RESPONSE_FIELDS.STATE_TOKEN])
          // Also store in DataStore for consistency
          DataStore.setItem(REGISTRATION_STORAGE_KEYS.STATE_TOKEN, data[API_RESPONSE_FIELDS.STATE_TOKEN])
        }

        if (onSuccess) await onSuccess(data)

        setValidationErrors([])
        clearError()

        if (submitStepName === StepName.MFA_VERIFY_CODE) {
          if (data?.[API_RESPONSE_FIELDS.STATUS] === OKTA_STATUS.SUCCESS) {
            localMfaAttemptsRef.current = 0
            setLocalMfaAttempts(0)
            dispatchAuthenticationAction({ type: EMkAuthenticationAction.RESET_FAILED_MFA_ATTEMPTS })

            if (data?.[API_RESPONSE_FIELDS.SESSION_TOKEN]) {
              DataStore.removeItem(REGISTRATION_STORAGE_KEYS.MFA_NEXT_LINK)

              // Store the session token and user data for the authentication service to use
              DataStore.setItem(REGISTRATION_STORAGE_KEYS.MFA_SESSION_TOKEN, data[API_RESPONSE_FIELDS.SESSION_TOKEN])
              if (data[API_RESPONSE_FIELDS.EMBEDDED]?.[API_RESPONSE_FIELDS.USER]) {
                DataStore.setItem(REGISTRATION_STORAGE_KEYS.MFA_USER, JSON.stringify(data[API_RESPONSE_FIELDS.EMBEDDED][API_RESPONSE_FIELDS.USER]))
              }

              // Use oktaAuth to authenticate the user and redirect to home
              if (oktaAuth) {
                try {
                  // Prepare PKCE params and persist an authenticated session prior to redirect
                  const { codeVerifier } = await oktaAuth.token.prepareTokenParams({ sessionToken: String(data[API_RESPONSE_FIELDS.SESSION_TOKEN]) })

                  const embeddedUser = data[API_RESPONSE_FIELDS.EMBEDDED]?.[API_RESPONSE_FIELDS.USER] || {}
                  const username = String(
                    (embeddedUser?.[API_RESPONSE_FIELDS.PROFILE] && embeddedUser[API_RESPONSE_FIELDS.PROFILE][API_RESPONSE_FIELDS.LOGIN]) ||
                      embeddedUser?.[API_RESPONSE_FIELDS.LOGIN] ||
                      state.formData.userName?.value ||
                      state.formData.email?.value ||
                      PLACEHOLDER_VALUES.UNKNOWN_USER,
                  )

                  const authenticatedSession = {
                    [SESSION_FIELDS.SESSION_TYPE]: SESSION_TYPES.AUTHENTICATED,
                    [SESSION_FIELDS.AUTH_BRAND]: brand,
                    [SESSION_FIELDS.AUTHENTICATED_USER]: embeddedUser || {},
                    [SESSION_FIELDS.CODE_VERIFIER]: String(codeVerifier),
                    [SESSION_FIELDS.SESSION_TOKEN]: String(data[API_RESPONSE_FIELDS.SESSION_TOKEN]),
                    [SESSION_FIELDS.USERNAME]: username,
                    [SESSION_FIELDS.RECOVERY_TOKEN]: '',
                  }

                  ProtectedDataStore.setItem(EMkStorageKeys.MAKO_SESSION, { ...authenticatedSession })

                  try {
                    DataStore.setItem(EMkStorageKeys.LOGIN_ORIGIN, NAVIGATION_PATHS.HOME)
                  } catch (_) {
                    // non-fatal
                  }

                  try {
                    DataStore.setItem(EMkStorageKeys.PENDING_REGISTRATION_MFA_LOGIN_SUCCESS, true)
                  } catch (_) {
                    // non-fatal
                  }

                  await oktaAuth.token.getWithRedirect({
                    sessionToken: String(data[API_RESPONSE_FIELDS.SESSION_TOKEN]),
                    codeVerifier: String(codeVerifier),
                  })

                  return
                } catch (authError) {
                  try {
                    DataStore.removeItem(EMkStorageKeys.PENDING_REGISTRATION_MFA_LOGIN_SUCCESS)
                  } catch (_) {
                    // non-fatal
                  }
                  navigate(NAVIGATION_PATHS.HOME, { replace: true })
                }
              } else {
                window.location.href = NAVIGATION_PATHS.HOME
              }
            } else {
              setError(REGISTER_LABELS.AUTHENTICATION_INCOMPLETE, new Error('No session token'), data)
            }
          } else if (data?.[API_RESPONSE_FIELDS.STATUS] === OKTA_STATUS.MFA_ENROLL_ACTIVATE) {
            setError(REGISTER_LABELS.MFA_SETUP_ERROR, new Error('Unexpected activation required'), data)
          } else if (data?.[API_RESPONSE_FIELDS.STATUS] === OKTA_STATUS.MFA_CHALLENGE) {
            const currentContext = loadRegistrationContext() || ({} as Partial<RegistrationContext>)

            const resendLink = Array.isArray(data?.[API_RESPONSE_FIELDS.LINKS]?.resend)
              ? data[API_RESPONSE_FIELDS.LINKS].resend[0]?.[API_RESPONSE_FIELDS.HREF]
              : data?.[API_RESPONSE_FIELDS.LINKS]?.resend?.[API_RESPONSE_FIELDS.HREF]

            const verifyLink = Array.isArray(data?.[API_RESPONSE_FIELDS.LINKS]?.[API_RESPONSE_FIELDS.VERIFY])
              ? data[API_RESPONSE_FIELDS.LINKS][API_RESPONSE_FIELDS.VERIFY][0]?.[API_RESPONSE_FIELDS.HREF]
              : data?.[API_RESPONSE_FIELDS.LINKS]?.[API_RESPONSE_FIELDS.VERIFY]?.[API_RESPONSE_FIELDS.HREF]

            const factor = data?.[API_RESPONSE_FIELDS.EMBEDDED]?.[API_RESPONSE_FIELDS.FACTOR]
            if (factor) {
              const updatedContext: Omit<RegistrationContext, 'createdAt' | 'expiresAt'> = {
                ...currentContext,
                selectedFactor: factor,
                factorProfile: factor.profile,
                mfaEmail: factor.factorType === 'email' ? factor.profile?.email : undefined,
                phone: factor.factorType === 'sms' || factor.factorType === 'call' ? factor.profile?.phoneNumber : undefined,
                resendLink: mergeRegistrationMfaResendHref(resendLink, verifyLink, currentContext.resendLink),
                verifyLink: verifyLink || currentContext.verifyLink,
                stateToken: currentContext.stateToken || data?.[API_RESPONSE_FIELDS.STATE_TOKEN] || '',
                activationToken: currentContext.activationToken || '',
                registrationToken: currentContext.registrationToken || '',
                email: currentContext.email || '',
                userName: currentContext.userName || '',
              }

              saveRegistrationContext(updatedContext)
            } else if (resendLink || verifyLink) {
              const updatedContext: Omit<RegistrationContext, 'createdAt' | 'expiresAt'> = {
                ...currentContext,
                resendLink: mergeRegistrationMfaResendHref(resendLink, verifyLink, currentContext.resendLink),
                verifyLink: verifyLink || currentContext.verifyLink,
                stateToken: currentContext.stateToken || data?.[API_RESPONSE_FIELDS.STATE_TOKEN] || '',
                activationToken: currentContext.activationToken || '',
                registrationToken: currentContext.registrationToken || '',
                email: currentContext.email || '',
                userName: currentContext.userName || '',
              }
              saveRegistrationContext(updatedContext)
            }
          } else if (data?.[API_RESPONSE_FIELDS.STATUS] === OKTA_STATUS.PENDING) {
            const currentContext = loadRegistrationContext() || ({} as Partial<RegistrationContext>)
            const resendLink = Array.isArray(data?.[API_RESPONSE_FIELDS.LINKS]?.resend)
              ? data[API_RESPONSE_FIELDS.LINKS].resend[0]?.[API_RESPONSE_FIELDS.HREF]
              : data?.[API_RESPONSE_FIELDS.LINKS]?.resend?.[API_RESPONSE_FIELDS.HREF]

            const verifyLink = Array.isArray(data?.[API_RESPONSE_FIELDS.LINKS]?.[API_RESPONSE_FIELDS.VERIFY])
              ? data[API_RESPONSE_FIELDS.LINKS][API_RESPONSE_FIELDS.VERIFY][0]?.[API_RESPONSE_FIELDS.HREF]
              : data?.[API_RESPONSE_FIELDS.LINKS]?.[API_RESPONSE_FIELDS.VERIFY]?.[API_RESPONSE_FIELDS.HREF]

            if (resendLink || verifyLink) {
              const updatedContext: Omit<RegistrationContext, 'createdAt' | 'expiresAt'> = {
                ...currentContext,
                resendLink: mergeRegistrationMfaResendHref(resendLink, verifyLink, currentContext.resendLink),
                verifyLink: verifyLink || currentContext.verifyLink,
                stateToken: currentContext.stateToken || data?.[API_RESPONSE_FIELDS.STATE_TOKEN] || '',
                activationToken: currentContext.activationToken || '',
                registrationToken: currentContext.registrationToken || '',
                email: currentContext.email || '',
                userName: currentContext.userName || '',
              }
              saveRegistrationContext(updatedContext)
            }
          } else if (data?.[API_RESPONSE_FIELDS.STATUS] === OKTA_STATUS.LOCKED_OUT || data?.[API_RESPONSE_FIELDS.STATUS] === OKTA_STATUS.PASSWORD_EXPIRED) {
            setError(REGISTER_LABELS.ACCOUNT_ISSUE_CONTACT_SUPPORT, new Error(data[API_RESPONSE_FIELDS.STATUS]), data)
          } else {
            setError(REGISTER_LABELS.INVALID_CODE_MESSAGE, new Error(data?.[API_RESPONSE_FIELDS.STATUS] || 'Unknown error'), data)
            dispatchAuthenticationAction({ type: EMkAuthenticationAction.INCREMENT_FAILED_MFA_ATTEMPTS })
          }
        } else if (submitStepName === StepName.USER_DETAILS) {
          // Note: Email is NOT saved to localStorage for security reasons
          if (state.registrationToken && state.formData.userName?.value) {
            try {
              saveRegistrationContext({
                registrationToken: state.registrationToken,
                userName: state.formData.userName.value as string,
                firstName: (state.formData.firstName?.value as string) || '',
                lastName: (state.formData.lastName?.value as string) || '',
                brand,
                activationToken: PLACEHOLDER_VALUES.USER_DETAILS,
                stateToken: PLACEHOLDER_VALUES.USER_DETAILS,
              })
            } catch {
              /* best-effort persistence */
            }
          }

          const stepNames = getStepNames()
          const currentIndex = stepNames.indexOf(state.currentStepName)
          if (currentIndex < stepNames.length - 1) {
            const nextStep = stepNames[currentIndex + 1]
            gotoStepByName(nextStep)
          }
        }
        // Check if this is the setPassword step - determine next flow based on response
        else if (submitStepName === StepName.SET_PASSWORD) {
          // Helper function to extract email from reset_password response
          const extractEmailFromResponse = (responseData: any): string | null => {
            // Try multiple locations where backend might return email
            // Backend should return email stored from /account/register call
            const email =
              responseData?._embedded?.user?.email || responseData?._embedded?.user?.profile?.email || responseData?.user?.email || responseData?.user?.profile?.email || null

            return email || null
          }

          // Extract email from response when MFA_ENROLL status is detected
          // This is critical for cross-device flows where formData.email won't be available
          if (data?.[API_RESPONSE_FIELDS.STATUS] === OKTA_STATUS.MFA_ENROLL) {
            // Extract email from response using helper (backend should return email stored from `/account/register`)
            // Critical for cross-device flows: On a different device, `formData.email` won't exist
            // Backend response is the primary source when user switches devices
            const emailFromResponse = extractEmailFromResponse(data)

            // If email found in response and `formData.email` is missing, set it
            // This handles cross-device scenarios where formData.email is not available
            if (emailFromResponse && !state.formData.email?.value) {
              updateField('email', emailFromResponse)
            }
          }

          const embeddedFactors = (data as any)?.[API_RESPONSE_FIELDS.EMBEDDED]?.[API_RESPONSE_FIELDS.FACTORS]

          // Check if MFA is required based on the response or form data
          // Check form data as it may have been set directly via updateField from MkPageActivate
          const hasMfaRequiredInFormData = state.formData.mfaRequired?.value === true
          const requiresMfa =
            data?.requiresMfa ||
            data?.mfaRequired ||
            data?.nextStep === 'mfa' ||
            data?.mfaSetupRequired ||
            data?.[API_RESPONSE_FIELDS.STATUS] === OKTA_STATUS.MFA_ENROLL ||
            hasMfaRequiredInFormData

          const hasMfaFactors =
            (data?.mfaFactors && Array.isArray(data.mfaFactors) && data.mfaFactors.length > 0) || (embeddedFactors && Array.isArray(embeddedFactors) && embeddedFactors.length > 0)

          if (requiresMfa || hasMfaFactors) {
            // Get the current registration context if available
            const currentContext = loadRegistrationContext() || {}
            const embeddedUser = ((data as any)?._embedded && (data as any)._embedded.user) || (data as any)?.user || {}
            const apiFirstName = embeddedUser?.profile?.firstName || embeddedUser?.firstName || ''
            const apiLastName = embeddedUser?.profile?.lastName || embeddedUser?.lastName || ''

            // Load MFA factors from multiple sources with priority, including registration context
            const mfaFactors = loadMfaFactorsFromMultipleSources(data, currentContext)
            if (mfaFactors.length > 0) {
              setMfaFactors(mfaFactors)
            }

            // Update registration context with MFA data instead of clearing it
            try {
              // Make sure we have all required fields for the context
              // Note: Email is NOT saved to localStorage for security reasons
              // Email remains in React state (formData.email) during the session
              const contextToSave: Omit<RegistrationContext, 'createdAt' | 'expiresAt'> = {
                ...(currentContext as any),
                mfaFactors: mfaFactors.length > 0 ? mfaFactors : undefined,
                stateToken: data?.[API_RESPONSE_FIELDS.STATE_TOKEN] || (currentContext as any)[REGISTRATION_STORAGE_KEYS.STATE_TOKEN] || '',
                activationToken: (currentContext as any).activationToken || '',
                registrationToken: state.registrationToken || (currentContext as any).registrationToken || '',
                userName: (state.formData.userName?.value as string) || (currentContext as any).userName || '',
                firstName: (state.formData.firstName?.value as string) || (currentContext as any).firstName || apiFirstName,
                lastName: (state.formData.lastName?.value as string) || (currentContext as any).lastName || apiLastName,
                // Email is intentionally NOT saved to localStorage - it remains in formData.email
                brand,
              }

              // Verify email is not being saved
              const hasEmailInContext = 'email' in contextToSave

              if (hasEmailInContext) {
                delete (contextToSave as any).email
              }

              saveRegistrationContext(contextToSave)
            } catch {
              /* best-effort persistence */
            }

            gotoStepByName(StepName.MFA_SETUP_SELECTOR)
          } else {
            try {
              const hasSessionToken = typeof data?.[API_RESPONSE_FIELDS.SESSION_TOKEN] === 'string' && data[API_RESPONSE_FIELDS.SESSION_TOKEN].length > 0
              const embeddedUser =
                ((data as any)?.[API_RESPONSE_FIELDS.EMBEDDED] && (data as any)[API_RESPONSE_FIELDS.EMBEDDED][API_RESPONSE_FIELDS.USER]) ||
                (data as any)[API_RESPONSE_FIELDS.USER] ||
                {}

              if (hasSessionToken && oktaAuth) {
                // Prepare PKCE params and persist an authenticated session prior to redirect
                const { codeVerifier } = await oktaAuth.token.prepareTokenParams({ sessionToken: String(data[API_RESPONSE_FIELDS.SESSION_TOKEN]) })

                const username = String(
                  (embeddedUser?.[API_RESPONSE_FIELDS.PROFILE] && embeddedUser[API_RESPONSE_FIELDS.PROFILE][API_RESPONSE_FIELDS.LOGIN]) ||
                    embeddedUser?.[API_RESPONSE_FIELDS.LOGIN] ||
                    (state as any)?.formData?.userName?.value ||
                    (state as any)?.formData?.email?.value ||
                    PLACEHOLDER_VALUES.UNKNOWN_USER,
                )

                const authenticatedSession = {
                  [SESSION_FIELDS.SESSION_TYPE]: SESSION_TYPES.AUTHENTICATED,
                  [SESSION_FIELDS.AUTH_BRAND]: brand,
                  [SESSION_FIELDS.AUTHENTICATED_USER]: embeddedUser || {},
                  [SESSION_FIELDS.CODE_VERIFIER]: String(codeVerifier),
                  [SESSION_FIELDS.SESSION_TOKEN]: String(data[API_RESPONSE_FIELDS.SESSION_TOKEN]),
                  [SESSION_FIELDS.USERNAME]: username,
                  [SESSION_FIELDS.RECOVERY_TOKEN]: '',
                }

                ProtectedDataStore.setItem(EMkStorageKeys.MAKO_SESSION, { ...authenticatedSession })

                try {
                  DataStore.setItem(EMkStorageKeys.LOGIN_ORIGIN, NAVIGATION_PATHS.HOME)
                } catch (_) {
                  // non-fatal
                }

                try {
                  // Before doing the redirect, make sure we have actually stored the session data
                  const storedSession = ProtectedDataStore.getItem(EMkStorageKeys.MAKO_SESSION)

                  if (!storedSession) {
                    // Try one more time with a more persistent approach
                    ProtectedDataStore.setItem(EMkStorageKeys.MAKO_SESSION, { ...authenticatedSession })
                  }

                  // Clear any cached Okta tokens/sessions before OAuth redirect
                  // This prevents stale sessions from interfering with password reset flow
                  await oktaAuth.tokenManager.clear()
                  DataStore.bulkRemoveItems([OKTA_CACHE_STORAGE, OKTA_SHARED_TRANSACTION_STORAGE, OKTA_ORIGINAL_URI_STORAGE, OKTA_TOKEN_STORAGE, OKTA_TOKEN_STORAGE_KEYS], {
                    store: localStorage,
                  })

                  // Now proceed with the token exchange
                  await oktaAuth.token.getWithRedirect({
                    sessionToken: String(data[API_RESPONSE_FIELDS.SESSION_TOKEN]),
                    codeVerifier: String(codeVerifier),
                  })
                } catch (redirectError) {
                  // If redirect fails, fall back to manual navigation
                  navigate(NAVIGATION_PATHS.HOME, { replace: true })
                }

                return
              }

              // Fallback: No sessionToken, still try to complete login or redirect to home
              showSnackbar({
                message: REGISTER_LABELS.REGISTRATION_COMPLETE,
                type: 'success',
                duration: TIME_CONSTANTS.SNACKBAR_SHORT_DURATION,
              })

              // Clear registration context from storage since registration is complete
              clearRegistrationContext()

              // Redirect to home page
              navigate(NAVIGATION_PATHS.HOME, { replace: true })
            } catch (sessionError) {
              showSnackbar({
                message: REGISTER_LABELS.REGISTRATION_COMPLETE_REDIRECT,
                type: 'success',
                duration: TIME_CONSTANTS.SNACKBAR_SHORT_DURATION,
              })

              // Clear registration context and redirect to home page
              clearRegistrationContext()

              // Allow time for the snackbar to show before redirecting
              setTimeout(() => {
                window.location.href = NAVIGATION_PATHS.HOME
              }, TIME_CONSTANTS.REDIRECT_DELAY_MS)
            }
          }
        }
        // Check if this is the MFA setup selector step
        else if (submitStepName === StepName.MFA_SETUP_SELECTOR) {
          // MFA setup selector completed

          // Try to reload MFA factors in case they weren't loaded earlier
          const mfaFactors = loadMfaFactorsFromMultipleSources(data)
          if (mfaFactors.length > 0) {
            setMfaFactors(mfaFactors)
          }

          // For email factor with existing email, the component handles navigation directly
          // Only auto-navigate to MFA_SETUP_DETAILS if not already navigated by component
          // Check if we should skip MFA_SETUP_DETAILS for email factors with valid email
          const isEmailFactor = state.selectedFactor?.factorType === 'email'
          const regCtx = loadRegistrationContext()
          const emailValue = String(state.formData.email?.value || state.formData.mfaEmail?.value || '')
          const ctxEmail = String(regCtx?.email || regCtx?.mfaEmail || '')
          const hasValidEmail = (emailValue && String(emailValue).includes('@') && emailValue.indexOf('@') > 0) || (ctxEmail && ctxEmail.includes('@') && ctxEmail.indexOf('@') > 0)

          // If email factor with valid email, skip navigation (component handles it)
          if (isEmailFactor && hasValidEmail) {
            return
          }

          // Navigate to MFA setup details for non-email factors or email without valid email
          gotoStepByName(StepName.MFA_SETUP_DETAILS)
        }
        // Check if this is the MFA setup details step
        else if (submitStepName === StepName.MFA_SETUP_DETAILS) {
          // MFA setup details completed

          // Extract and store the factorId from the Okta API response
          // The factorId can be in several locations depending on the response structure
          let factorId = data?.id || data?.[API_RESPONSE_FIELDS.FACTOR_ID]

          // If not found at top level, check _embedded structure
          if (!factorId && data?.[API_RESPONSE_FIELDS.EMBEDDED]) {
            // Check if _embedded.factor is an array (most common case)
            if (Array.isArray(data[API_RESPONSE_FIELDS.EMBEDDED][API_RESPONSE_FIELDS.FACTOR])) {
              // Find the factor that matches our selected factor type
              const selectedFactorType = state.selectedFactor?.factorType

              // Look for the factor that matches our selected type
              const matchingFactor = data[API_RESPONSE_FIELDS.EMBEDDED][API_RESPONSE_FIELDS.FACTOR].find((factor: { factorType: any }) => factor.factorType === selectedFactorType)

              if (matchingFactor) {
                factorId = matchingFactor.id
              } else {
                // If no exact match, take the first factor (fallback)
                factorId = data[API_RESPONSE_FIELDS.EMBEDDED][API_RESPONSE_FIELDS.FACTOR][0]?.id
              }
            } else {
              // Handle single factor object (less common)
              factorId =
                data[API_RESPONSE_FIELDS.EMBEDDED][API_RESPONSE_FIELDS.FACTOR]?.id ||
                data[API_RESPONSE_FIELDS.EMBEDDED][API_RESPONSE_FIELDS.FACTORS]?.[0]?.id ||
                data[API_RESPONSE_FIELDS.EMBEDDED][API_RESPONSE_FIELDS.FACTOR]?.[API_RESPONSE_FIELDS.FACTOR_ID] ||
                data[API_RESPONSE_FIELDS.EMBEDDED][API_RESPONSE_FIELDS.FACTORS]?.[0]?.[API_RESPONSE_FIELDS.FACTOR_ID]
            }
          }

          if (factorId) {
            updateField('mfaFactorId', factorId)
          } else if (data?.[API_RESPONSE_FIELDS.STATUS] === OKTA_STATUS.MFA_ENROLL_ACTIVATE && data?.[API_RESPONSE_FIELDS.LINKS]) {
            // For MFA_ENROLL_ACTIVATE status, we might need to extract from _links (factor ID in _links.verify.href)
            const verifyLink = data[API_RESPONSE_FIELDS.LINKS]?.[API_RESPONSE_FIELDS.VERIFY]?.[API_RESPONSE_FIELDS.HREF]
            if (verifyLink) {
              const match = verifyLink.match(/\/factors\/([^/]+)\/verify/)
              if (match && match[1]) {
                const [, factorIdToUse] = match
                updateField('mfaFactorId', factorIdToUse)
              }
            }
          }

          // Extract and store the stateToken from the Okta API response
          const stateToken = data?.[API_RESPONSE_FIELDS.STATE_TOKEN]
          if (stateToken) {
            updateField(REGISTRATION_STORAGE_KEYS.STATE_TOKEN, stateToken)
            // Also store in DataStore for compatibility
            DataStore.setItem(REGISTRATION_STORAGE_KEYS.STATE_TOKEN, stateToken)
          }

          // Store the verify link for MFA verification step
          const verifyLink = data?.[API_RESPONSE_FIELDS.LINKS]?.[API_RESPONSE_FIELDS.NEXT]?.[API_RESPONSE_FIELDS.HREF]
          if (verifyLink) {
            // Store in DataStore for backward compatibility
            DataStore.setItem(REGISTRATION_STORAGE_KEYS.MFA_NEXT_LINK, verifyLink)
          }

          // Save MFA data to registration context
          const currentContext = loadRegistrationContext()
          if (currentContext && factorId && (stateToken || verifyLink)) {
            saveRegistrationContext({
              ...currentContext,
              mfaFactorId: factorId,
              stateToken: stateToken || currentContext.stateToken,
              verifyLink: verifyLink || currentContext.verifyLink,
              selectedFactor: state.selectedFactor,
              mfaRequired: true,
              mfaEmail: (state.formData.mfaEmail?.value as string) || currentContext.mfaEmail,
              phone: (state.formData.phone?.value as string) || currentContext.phone,
            })
          }

          // Navigate to MFA verification

          // Check if we have a valid factor ID before proceeding
          // Use the factorId we just extracted, not the stored value (timing issue)
          if (!factorId || factorId === PLACEHOLDER_VALUES.FACTOR_ID) {
            setError(REGISTER_LABELS.INVALID_FACTOR_ID, new Error('No valid factor ID'), data)
            return
          }

          gotoStepByName(StepName.MFA_VERIFY_CODE)
        } else {
          // General case: advance to next step in sequence
          const stepNames = getStepNames()
          const currentIndex = stepNames.indexOf(state.currentStepName)
          if (currentIndex < stepNames.length - 1) {
            const nextStep = stepNames[currentIndex + 1]
            gotoStepByName(nextStep)
          }
        }
      } catch (err) {
        const apiError = err as any

        const fieldErrors: Array<{ field: string; message: string }> = apiError?.errors || []
        if (Array.isArray(fieldErrors) && fieldErrors.length) {
          fieldErrors.forEach((fe) => fe?.field && setFieldError(fe.field, fe.message || 'Invalid'))
        } else {
          const code = apiError?.code || apiError?.errorCode || apiError?.message
          if (typeof code === 'string') {
            if (code.toUpperCase().includes(ERROR_CHECK_FIELDS.EMAIL)) setFieldError('email', apiError?.message || 'Email is invalid')
            if (code.toUpperCase().includes(ERROR_CHECK_FIELDS.USERNAME) || code.toUpperCase().includes(ERROR_CHECK_FIELDS.USER_NAME))
              setFieldError('userName', apiError?.message || 'Username is invalid')
          }
        }

        if (apiError?.code === 'MFA_PENDING' || apiError?.message?.includes('Verification pending')) {
          if (state.currentStepName !== StepName.MFA_VERIFY_CODE) {
            showSnackbar({
              message: REGISTER_LABELS.MFA_VERIFICATION_FAILED,
              type: 'error',
              duration: TIME_CONSTANTS.SNACKBAR_SHORT_DURATION,
            })
          } else {
            setFieldError('verificationCode', REGISTER_LABELS.MFA_VERIFICATION_FAILED)
            clearError()
          }
          return
        }

        if (apiError?.errorCode === RECOVERY_ERROR_CODES.OKTA_AUTH_STATE_ERROR || apiError?.code === RECOVERY_ERROR_CODES.OKTA_AUTH_STATE_ERROR) {
          const errorMessage = apiError?.errorSummary || apiError?.message || 'Authentication state error. Please try again.'
          if (state.currentStepName === StepName.MFA_VERIFY_CODE) {
            setFieldError('verificationCode', errorMessage)
            clearError()
          } else {
            setError(errorMessage, apiError as Error, apiError?.voyager)

            showSnackbar({
              message: REGISTER_LABELS.AUTH_ERROR_REFRESH,
              type: 'error',
              duration: TIME_CONSTANTS.SNACKBAR_LONG_DURATION,
            })
          }
          return
        }

        persistMfaChallengeFromError(apiError)

        const action = classifyMfaVerifyError(apiError, localMfaAttemptsRef.current)
        switch (action.kind) {
          case EMfaVerifyActionKind.Expired:
            setFieldError('verificationCode', REGISTER_LABELS.MFA_VERIFICATION_CODE_EXPIRED)
            return
          case EMfaVerifyActionKind.InvalidPasscode: {
            dispatchAuthenticationAction({ type: EMkAuthenticationAction.INCREMENT_FAILED_MFA_ATTEMPTS })
            localMfaAttemptsRef.current = action.nextAttemptCount
            setLocalMfaAttempts(action.nextAttemptCount)
            const message =
              action.nextAttemptCount === VALIDATION_THRESHOLDS.MFA_LOCKOUT_MAX_ATTEMPTS - 1 ? REGISTER_LABELS.MFA_BEFORE_ACCOUNT_LOCKOUT_WARNING : REGISTER_LABELS.INVALID_CODE_MESSAGE
            setFieldError('verificationCode', message)
            clearError()
            return
          }
          case EMfaVerifyActionKind.Lockout: {
            clearError()
            const lockoutEndTime = Date.now() + TIME_CONSTANTS.LOCKOUT_DURATION_MS
            const username = (state.formData.email?.value as string) || (state.formData.userName?.value as string) || ''
            dispatchAuthenticationAction({
              type: EMkAuthenticationAction.LOCK_MFA_ACCOUNT,
              payload: { lockoutEndTime, username },
            })
            setCurrentStep(StepName.MFA_CRITICAL_LOCK_SCREEN)
            return
          }
          case EMfaVerifyActionKind.SessionExpired: {
            const errorMessage = REGISTER_LABELS.SESSION_EXPIRED
            setError(errorMessage, apiError as Error, apiError?.voyager)
            showSnackbar({
              message: errorMessage,
              type: 'error',
              duration: TIME_CONSTANTS.SNACKBAR_LONG_DURATION,
            })
            clearRegistrationContext()
            return
          }
          case EMfaVerifyActionKind.Unknown: {
            const errorMessage = action.message
            if (state.currentStepName === StepName.MFA_VERIFY_CODE) {
              setFieldError('verificationCode', errorMessage)
              clearError()
            } else {
              showSnackbar({
                message: REGISTER_LABELS.REGISTRATION_FAILED,
                type: 'error',
                duration: TIME_CONSTANTS.SNACKBAR_LONG_DURATION,
              })
              setError(errorMessage, apiError as Error, apiError?.voyager)
              setTimeout(() => {
                clearError()
              }, TIME_CONSTANTS.ERROR_AUTO_CLEAR_MS)
            }
            return
          }
          default: {
            const exhaustiveCheck: never = action
            throw new Error(`Unexpected MFA verify classify action: ${JSON.stringify(exhaustiveCheck)}`)
          }
        }
      } finally {
        mfaVerifySubmitInFlightRef.current = false
        setLoading(false)
      }
    },
    [
      state,
      brand,
      setLoading,
      setError,
      clearError,
      setValidationErrors,
      setRegistrationToken,
      showSnackbar,
      setFieldError,
      setCurrentStep,
      gotoStepByName,
      dispatchAuthenticationAction,
      clearRegistrationContext,
      persistMfaChallengeFromError,
      loadRegistrationContext,
      navigate,
    ],
  )

  const value = React.useMemo(
    () => ({
      state,
      canSubmit,
      updateField,
      setFieldTouched,
      gotoStepByName,
      setSelectedFactor,
      setMfaFactors,
      setUser,
      setMfaEnabled,
      registrationMfaEnabled,
      setCurrentStep,
      submit,
      resetState,
      loadRegistrationContext,
      setRegistrationToken,
      setValidationErrors,
      setFieldError,
      clearError,
      setError,
      saveRegistrationContext,
      clearRegistrationContext,
      dispatch,
      gotoStep,
      getStepIndexByName,
      gotoActivationError,
      hasValidRegistrationContext,
      setLoading,
      setSubmitted,
    }),
    [
      state,
      updateField,
      setFieldTouched,
      setSelectedFactor,
      setMfaFactors,
      setUser,
      setMfaEnabled,
      registrationMfaEnabled,
      setCurrentStep,
      submit,
      canSubmit,
      resetState,
      loadRegistrationContext,
      setRegistrationToken,
      setValidationErrors,
      setFieldError,
      clearError,
      setError,
      saveRegistrationContext,
      clearRegistrationContext,
      dispatch,
      gotoStep,
      getStepIndexByName,
      gotoActivationError,
      hasValidRegistrationContext,
      setLoading,
      setSubmitted,
    ],
  )

  /**
   * DevUI hooks removed - using direct startStep parameter instead
   *
   * Three useEffect hooks were removed here that were interfering with the modal's startStep prop
   * by overriding the current step. The DevUI Panel now passes the selected step directly via
   * openRegisterModal(undefined, selectedStepKey) which sets the startStep prop on the Register
   * component, making those hooks unnecessary and problematic.
   *
   * The removed hooks were:
   * 1. dev.applyOnLoad hook - set step based on dev.startStepName
   * 2. dev.applyNowTick hook - reapplied dev.startStepName on dev.applyNowTick changes
   * 3. dev.lastSelectedStep hook - synced step with dev.lastSelectedStep
   */
  return <RegisterContext.Provider value={value}>{children}</RegisterContext.Provider>
}
